<template>
  <div class="results-result-card f-body-1">
    <div v-for="item in results" :key="item._id">
      <SearchResultCard
        v-if="showCard(item)"
        class="results-result-card__body"
        :title="setTitle(item)"
        :description="setDescription(item)"
        :image="setSrc(item)"
        :url="item._source.url"
        :showImages="featureImages"
        :showTopics="featureTags"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "GlobalSearchResults",
  data() {
    return {
      listLimit: 5,
      routeName: "none",
      excludedPatterns: [
        "default-social-media-image",
        "getty_logo_og",
        "favicon",
        "gravatar"
      ]
    };
  },
  props: {
    featureImages: Boolean,
    featureTags: Boolean,
    results: Array,
    showImages: Boolean
  },
  methods: {
    /**
     * A Result Item
     * @typedef    {Object} Item
     * @property   {String} _id       - id of Object
     * @property   {String} _index    - index of item
     * @property   {Number} _score    - relevant score of item
     * @property   {Object} _source   - relevant data of item
     * @property   {Object} highlight - Object of highlighted data relevant to query
     * @property   {String} _type     - type of document
     */

    /**
     * Check if card should be shown or not
     *
     * @param     {Object} - result item
     *
     * @description check the title and description for various results and then
     * check the length and make sure it exists and returns true, otherwise logs to
     * console with the id of the item so it can be tracked and returns false.
     *
     * @return    {Boolean}
     */
    showCard(item) {
      const title = this.setTitle(item);
      const description = this.setDescription(item);
      if (!title.length || !description.length) {
        console.log(`Missing title and/or description at: ${item._source.id}`);
        return false;
      }
      return true;
    },
    /**
     * Filter descriptoin
     * @param      {Object} - result item
     *
     * @description filters highlights and result then sets appropriate one to description,
     * whichver it can find first, it returns it as a string
     *
     * @return     {String} Results passed into description and returned
     *
     * @example
     *  item = {
     *    "_index": "index",
     *    "_type": "doc",
     *    "_id": "http://www.getty.edu/",
     *    "_score": 4.5,
     *    "_source": {
     *      "metatag.og:site_name": "The J. Paul Getty in Los Angeles",
     *      "content": "//",
     *      "DESCRIPTION": "Visit our Los Angeles museums and library, interact with art, and access free research tools.",
     *      "segment": "20181005195723",
     *      "digest": "4b1d3e757f8e4a2d5bb5fa451597a745",
     *      "host": "www.getty.edu",
     *      "metatag.apple-mobile-web-app-capable": "yes",
     *      "id": "http://www.getty.edu/",
     *      "metatag.viewport": "width=device-width",
     *      "metatag.description": "Visit our Los Angeles museums and library, interact with art, and access free research tools.",
     *      "metatag.og:description": "Visit our Los Angeles museums and library, interact with art, and access free research tools.",
     *      "ROBOTS": "index,follow",
     *      "url": "http://www.getty.edu/",
     *      "metatag.og:image": "https://media.getty.edu/iiif/image/undefined/full/full/0/default.jpg",
     *      "metatag.og:title": "Getty: Resources for Visual Art and Cultural Heritage",
     *      "metatag.og:type": "website",
     *      "metatag.name": "Getty: Resources for Visual Art and Cultural Heritage",
     *      "tstamp": "Fri Oct 05 20:05:58 PDT 2018",
     *      "boost": "0.0",
     *      "title": "Getty: Resources for Visual Art and Cultural Heritage",
     *      "twitter:title": "Getty: Resources for Visual Art and Cultural Heritage",
     *      "twitter:description": "Visit our Los Angeles museums and library, interact with art, and access free research tools."
     *      "twitter:card": "summary",
     *      "twitter:site": "@GettyMuseum"
     *    }
     *  }
     *    setDescription(item) - would return "_source['metatag.description']"
     */
    setDescription(result) {
      let description = "";
      if (result?.highlight?.DESCRIPTION?.[0]) {
        description = result.highlight.DESCRIPTION[0];
      } else if (result?._source?.["metatag.description"]) {
        description = result._source["metatag.description"];
      } else if (result?._source?.["dcterms.description"]) {
        description = result._source["dcterms.description"];
      } else if (result?._source?.["metatag.og:description"]) {
        description = result._source["metatag.og:description"];
      } else if (result?._source?.DESCRIPTION) {
        description = result._source.DESCRIPTION;
      } else if (result?._source?.description) {
        description = result._source.description;
      } else if (result?.highlight?.content?.[0]) {
        description = result.highlight.content[0];
      } else {
        description = this.$options.filters.truncate(
          result._source.content,
          300
        );
      }
      return description;
    },
    /**
     * Filter title
     * @param      {Object} - result item
     *
     * @description filters highlights and result then sets appropriate one to title,
     * then whichever of those it can find first, it does a quick regex
     * to remove [] and then returns that to the function so it can be
     * called for each item.
     *
     * @return     {String} Results passed into description and returned
     *
     * @example
     *  item = {
     *    "_index": "index",
     *    "_type": "doc",
     *    "_id": "http://www.getty.edu/",
     *    "_score": 4.5,
     *    "_source": {
     *      "metatag.og:site_name": "The J. Paul Getty in Los Angeles",
     *      "content": "//",
     *      "DESCRIPTION": "Visit our Los Angeles museums and library, interact with art, and access free research tools.",
     *      "segment": "20181005195723",
     *      "digest": "4b1d3e757f8e4a2d5bb5fa451597a745",
     *      "host": "www.getty.edu",
     *      "metatag.apple-mobile-web-app-capable": "yes",
     *      "id": "http://www.getty.edu/",
     *      "metatag.viewport": "width=device-width",
     *      "ROBOTS": "index,follow",
     *      "url": "http://www.getty.edu/",
     *      "metatag.og:image": "https://media.getty.edu/iiif/image/undefined/full/full/0/default.jpg",
     *      "metatag.og:title": "Getty: Resources for Visual Art and Cultural Heritage",
     *      "metatag.og:type": "website",
     *      "metatag.name": "Getty: Resources for Visual Art and Cultural Heritage",
     *      "tstamp": "Fri Oct 05 20:05:58 PDT 2018",
     *      "boost": "0.0",
     *      title": "Getty: Resources for Visual Art and Cultural Heritage",
     *      "twitter:title": "Getty: Resources for Visual Art and Cultural Heritage",
     *      "twitter:description": "Visit our Los Angeles museums and library, interact with art, and access free research tools."
     *      "twitter:card": "summary",
     *      "twitter:site": "@GettyMuseum"
     *    }
     *  }
     *    setTitle(item) - would return "_source.title"
     */
    setTitle(result) {
      let title = "";
      if (result?.highlight?.title?.[0]) {
        title = result.highlight.title[0];
      } else if (result?._source?.title) {
        title = result._source.title;
      } else if (result?._source?.["metatag.og:title"]) {
        title = result._source["metatag.og:title"];
      } else if (result?._source?.["metatag.title"]) {
        title = result._source["metatag.title"];
      } else {
        title = this.$options.filters.truncate(result._source.content, 300);
      }
      return title.replace(/[\[\]']+/g, "");
    },
    /**
     * Filter image
     * @param      {Object} - result item
     *
     * @description Return the first available image source in order of preference:
     * `metatag.thumbimage` or `image`, also check if src includes
     *  one of the open graph default images: default-social-media-image
     *  or getty_logo_og; if it contains one of them, set to null.
     *
     * @return     {String}  Image `src` attribute string
     *
     * @example
     *  item = {
     *    "_index": "index",
     *    "_type": "doc",
     *    "_id": "http://www.getty.edu/",
     *    "_score": 4.5,
     *    "_source": {
     *      "metatag.og:site_name": "The J. Paul Getty in Los Angeles",
     *      "content": "//",
     *      "DESCRIPTION": "Visit our Los Angeles museums and library, interact with art, and access free research tools.",
     *      "segment": "20181005195723",
     *      "digest": "4b1d3e757f8e4a2d5bb5fa451597a745",
     *      "host": "www.getty.edu",
     *      "metatag.apple-mobile-web-app-capable": "yes",
     *      "id": "http://www.getty.edu/",
     *      "metatag.viewport": "width=device-width",
     *      "ROBOTS": "index,follow",
     *      "url": "http://www.getty.edu/",
     *      "metatag.og:image": "https://media.getty.edu/iiif/image/undefined/full/full/0/default.jpg",
     *      "boost": "0.0",
     *      "title": "Getty: Resources for Visual Art and Cultural Heritage",
     *      "image": "https://media.getty.edu/iiif/image/undefined/full/full/0/default.jpg"
     *    }
     *  }
     *    setSrc(item) - would return "_source['metatag.og:image']"
     */
    setSrc(result) {
      let src = result?._source
        ? result._source["metatag.thumbimage"] ||
          result._source["metatag.og:image"] ||
          result._source.image
        : null;
      return this.isPatternExcluded(src) ? null : src;
    },
    /**
     * filter image src through images to exclude
     *
     * @param     {String}       String of src
     *
     * @description Goes through the excludedPatterns and determines if
     * they match the src passed in, returns Boolean true/false
     *
     * @return    {Boolean}     Boolean value
     */
    isPatternExcluded(src) {
      if (!src) return null;
      return this.excludedPatterns.some(pattern =>
        src.match(new RegExp(pattern, "gi"))
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.results-result-card {
  &__body {
    border-top: 1px solid $color__grey--20;
    display: flex;
    padding: $spacing-03 0;
    &--text {
      max-width: 600px;
      padding-right: $spacing-03;
      .f-heading-3 {
        font-weight: 600;
      }
      &-description,
      &-topics {
        padding: $spacing-01 0;
      }
      &-description {
        a {
          font-weight: 400 !important;
        }
      }
    }
    &--image {
      border-left: 1px solid $color__grey--20;
      display: inline-flex;
      margin-left: auto;
      max-width: 350px;
      padding-left: $spacing-03;
    }
    a {
      color: $color__text;
      font-weight: 500;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
    &:last-child {
      border-bottom: 1px solid $color__grey--20;
    }
    @include breakpoint("small-") {
      display: block;
      &--image {
        border-left: none;
        display: block;
        margin: 0;
        max-width: 100%;
        padding: 0 $spacing-12;
        &.letterbox {
          &-black {
            background: $color__black;
          }
          &-white {
            background: $color__white;
          }
          &-transparent {
            background: transparent;
          }
          &-none {
            background: transparent;
            padding: 0;
          }
        }
      }
    }
  }
}
</style>
